import React from "react";
import { FaCircle } from "react-icons/fa";
import {
  FaFileCircleCheck,
  FaFileCircleQuestion,
  FaFileCircleExclamation,
} from "react-icons/fa6";

import {
  StatusType,
  TitleDocumentQuery,
} from "@Types/title_issues";
import { MutationError } from "@Types/index";

import { buildDocumentStatusQuery } from "../titleIssueStatusQueries";
import styles from "./TitleIssuesDocumentStatusCards.module.scss";

// Status configuration with icons, labels, and colors
const STATUS_CONFIG = {
  awaiting: {
    label: "Awaiting",
    icon: FaFileCircleQuestion,
    color: "#6b7280",
    activeColor: "#4f46e5",
  },
  incomplete: {
    label: "Incomplete",
    icon: FaFileCircleExclamation,
    color: "#ffbb6c",
    activeColor: "#d97706",
  },
  resolved: {
    label: "Uploaded",
    icon: FaFileCircleCheck,
    color: "#10b981",
    activeColor: "#059669",
  },
} as const;

interface TitleIssueStatusCardsProps {
  titleQuery: TitleDocumentQuery;
  handleSetFilter: (
    type: "document" | "dmv",
    query: TitleDocumentQuery
  ) => void;
  counts?: Record<StatusType | "not required", number>;
  isLoading: boolean;
  error: MutationError;
}

const TitleIssuesDocumentStatusCards: React.FC<TitleIssueStatusCardsProps> = ({
  titleQuery,
  handleSetFilter,
  counts,
  isLoading,
  error,
}) => {
  const statusCounts = counts ?? {
    awaiting: 0,
    "not required": 0,
    incomplete: 0,
    resolved: 0,
  };

  const handleStatusClick = (status: StatusType) => {
    if (status === titleQuery?.active) {
      // Deactivate current filter
      handleSetFilter("document", undefined);
    } else {
      // Set new filter
      handleSetFilter("document", {
        active: status,
        query: buildDocumentStatusQuery(status),
      } as TitleDocumentQuery);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, status: StatusType) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleStatusClick(status);
    }
  };

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>
          Failed to load status information. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.statusCardsContainer}>
      <h3 className={styles.sectionLabel}>Document Status Filter</h3>
      <div className={styles.statusCardsGrid}>
        {(Object.keys(STATUS_CONFIG) as StatusType[]).map((status) => {
          const config = STATUS_CONFIG[status];
          const count = statusCounts[status];
          const isActive = titleQuery?.active === status;
          const IconComponent = config.icon;

          return (
            <div
              key={status}
              className={`${styles.statusCard} ${styles[status]} ${
                isActive ? styles.active : ""
              } ${isLoading ? styles.loading : ""}`}
              onClick={() => !isLoading && handleStatusClick(status)}
              onKeyDown={(e) => !isLoading && handleKeyDown(e, status)}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              aria-label={`Filter by ${config.label} status, ${count} items`}
              aria-disabled={isLoading}
            >
              <div className={styles.statusCardContent}>
                <div className={styles.statusInfo}>
                  <div className={styles.statusIcon}>
                    <IconComponent
                      size={20}
                      color={isActive ? config.activeColor : config.color}
                      aria-hidden="true"
                    />
                  </div>
                  <div className={styles.statusText}>
                    <span className={styles.statusLabel}>{config.label}</span>
                    <span
                      className={styles.statusCount}
                      aria-label={`${count} items`}
                    >
                      ({count})
                    </span>
                  </div>
                </div>

                {isActive && (
                  <div className={styles.activeIndicator}>
                    <FaCircle
                      size={8}
                      color={config.activeColor}
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>

              {isLoading && (
                <div className={styles.loadingOverlay}>
                  <div className={styles.spinner} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TitleIssuesDocumentStatusCards;
